import React, { useEffect, useMemo, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";

import { fetchClubPublicBySlugGuest } from "../../api/clubApi";
import { getClubMiniWebsitePathPrefix } from "../../config/clubMiniWebsite";
import { getCloudinaryMediaBaseUrl } from "../../config/cloudinaryBase";
import { resolveCloudinaryImageSrc } from "../../helpers/helper.service";
import OrderTicketsView from "./OrderTicketsView";
import "./clubMiniTickets.css";

function unwrapClubPublic(raw) {
  if (raw == null) return null;
  if (raw.club && typeof raw.club === "object") return raw.club;
  const d = raw.data;
  if (d && typeof d === "object") {
    if (d.club) return d.club;
    if (d.name || d.slug || d.primarycolor) return d;
  }
  if (raw.name || raw.slug || raw.primarycolor) return raw;
  return null;
}

/**
 * Club mini-site: /{prefix}/{slug}/my-tickets/:orderId?token=
 * Prefix from REACT_APP_CLUB_MINI_WEBSITE_PATH_PREFIX (default club).
 */
const ClubMyTicketsContainer = () => {
  const { slug, orderId } = useParams();
  const [searchParams] = useSearchParams();
  const prefix = getClubMiniWebsitePathPrefix();

  const [club, setClub] = useState(null);
  const [clubState, setClubState] = useState("loading");

  useEffect(() => {
    let cancelled = false;
    if (!slug) {
      setClubState("error");
      return;
    }
    setClubState("loading");
    fetchClubPublicBySlugGuest(slug)
      .then((raw) => {
        const c = unwrapClubPublic(raw);
        if (!cancelled) {
          setClub(c);
          setClubState(c ? "ok" : "error");
        }
      })
      .catch(() => {
        if (!cancelled) {
          setClub(null);
          setClubState("error");
        }
      });
    return () => {
      cancelled = true;
    };
  }, [slug]);

  const loginReturnPath = useMemo(() => {
    const q = searchParams.toString();
    return `/${prefix}/${slug}/my-tickets/${orderId}${q ? `?${q}` : ""}`;
  }, [prefix, slug, orderId, searchParams]);

  const cloudinaryBase = getCloudinaryMediaBaseUrl();
  const logoRaw = club?.logo ? String(club.logo).trim() : "";
  const logoSrc =
    logoRaw &&
    ( /^https?:\/\//i.test(logoRaw) || logoRaw.startsWith("data:")
      ? logoRaw
      : resolveCloudinaryImageSrc(logoRaw, cloudinaryBase, ""));

  const clubLabel = club?.name || club?.slug || slug || "";
  const primary =
    typeof club?.primarycolor === "string" && club.primarycolor.trim()
      ? club.primarycolor.trim()
      : "#2d3748";

  const documentTitleSuffix =
    clubState === "ok" && clubLabel ? clubLabel : slug || undefined;

  return (
    <div
      className="my-tickets-page club-mini-tickets-page min-vh-100 py-3 py-sm-4 px-2"
      style={{
        background: `linear-gradient(180deg, ${primary}40 0%, #0a0a0c 38%, #08080a 100%)`,
      }}
    >
      <header className="club-mini-tickets-header text-center mb-3">
        {clubState === "ok" && logoSrc ? (
          <img src={logoSrc} alt="" className="club-mini-tickets-logo mb-2" />
        ) : null}
        <div className="club-mini-tickets-clublabel small text-uppercase">
          {clubState === "loading"
            ? "…"
            : clubState === "error"
              ? slug
              : clubLabel}
        </div>
      </header>
      <OrderTicketsView
        orderId={orderId}
        searchParams={searchParams}
        loginReturnPath={loginReturnPath}
        documentTitleSuffix={documentTitleSuffix}
      />
    </div>
  );
};

export default ClubMyTicketsContainer;
